import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faList, faBox } from "@fortawesome/free-solid-svg-icons";
import { Card } from "../../components/ui/card";
import { useProducts } from "../../hooks";
import { useAuth } from "../../hooks";

import ProductType from "./product/ProductType";
import ProductCategory from "./product/ProductCategory";
import Product from "./product/Product";

const ManageProduct = () => {
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "types";
  const [activeTab, setActiveTab] = useState(tabParam);

  const {
    productTypes,
    productTypesLoading,
    productTypesError,
    productTypesPagination,
    productTypesPage,
    setProductTypesPage,
    productTypesPageSize,
    setProductTypesPageSize,
    productTypesSearch,
    setProductTypesSearch,
    createProductType,
    updateProductType,
    toggleProductTypeStatus,
    refreshProductTypes,

    productCategories,
    productCategoriesLoading,
    productCategoriesError,
    productCategoriesPagination,
    productCategoriesPage,
    setProductCategoriesPage,
    productCategoriesPageSize,
    setProductCategoriesPageSize,
    productCategoriesSearch,
    setProductCategoriesSearch,
    createProductCategory,
    updateProductCategory,
    toggleProductCategoryStatus,
    refreshProductCategories,

    products,
    productsLoading,
    productsError,
    productsPagination,
    productsPage,
    setProductsPage,
    productsPageSize,
    setProductsPageSize,
    productsSearch,
    setProductsSearch,
    createProduct,
    updateProduct,
    toggleProductStatus,
    refreshProducts,
    getPackageTypes,
  } = useProducts();

  useEffect(() => {
    const tab = searchParams.get("tab") || "types";
    setActiveTab(tab);
  }, [searchParams]);

  const canViewTypes = hasPermission([
    "manage_product_type",
    "read_product_type",
    "manage_product",
    "read_product",
  ]);

  const canViewCategories = hasPermission([
    "manage_product_category",
    "read_product_category",
    "manage_product",
    "read_product",
  ]);

  const canViewProducts = hasPermission(["manage_product", "read_product"]);

  const allTabs = [
    {
      id: "types",
      label: "Product Types",
      icon: faLayerGroup,
      count: productTypesPagination?.total ?? productTypes?.length ?? 0,
      show: canViewTypes,
    },
    {
      id: "categories",
      label: "Product Categories",
      icon: faList,
      count:
        productCategoriesPagination?.total ?? productCategories?.length ?? 0,
      show: canViewCategories,
    },
    {
      id: "products",
      label: "Products",
      icon: faBox,
      count: productsPagination?.total ?? products?.length ?? 0,
      show: canViewProducts,
    },
  ];

  const tabs = allTabs.filter((t) => t.show);

  const currentTab = tabs.some((t) => t.id === activeTab)
    ? activeTab
    : tabs[0]?.id;

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
              Product Management
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Manage product types, categories, and products
            </p>
          </div>
        </div>
      </Card>

      {tabs.length > 0 && (
        <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full">
            {tabs.map((tab) => {
              const isActive = currentTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 
                    px-2 sm:px-4 py-1.5 sm:py-2 rounded-md 
                    text-[10px] sm:text-sm font-medium transition-all duration-200 
                    flex-1
                    ${
                      isActive
                        ? "bg-blue-800 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                    }
                  `}
                >
                  <FontAwesomeIcon
                    icon={tab.icon}
                    className={`h-4 w-4 sm:h-3.5 sm:w-3.5 ${isActive ? "text-white" : "text-gray-600"}`}
                  />
                  <span className="text-center leading-tight">{tab.label}</span>
                  <span
                    className={`hidden md:inline-block text-[12px] px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {currentTab === "types" && (
        <ProductType
          productTypes={productTypes}
          productTypesLoading={productTypesLoading}
          productTypesError={productTypesError}
          productTypesPagination={productTypesPagination}
          productTypesPage={productTypesPage}
          setProductTypesPage={setProductTypesPage}
          productTypesPageSize={productTypesPageSize}
          setProductTypesPageSize={setProductTypesPageSize}
          productTypesSearch={productTypesSearch}
          setProductTypesSearch={setProductTypesSearch}
          createProductType={createProductType}
          updateProductType={updateProductType}
          toggleProductTypeStatus={toggleProductTypeStatus}
          refreshProductTypes={refreshProductTypes}
        />
      )}

      {currentTab === "categories" && (
        <ProductCategory
          productCategories={productCategories}
          productCategoriesLoading={productCategoriesLoading}
          productCategoriesError={productCategoriesError}
          productCategoriesPagination={productCategoriesPagination}
          productCategoriesPage={productCategoriesPage}
          setProductCategoriesPage={setProductCategoriesPage}
          productCategoriesPageSize={productCategoriesPageSize}
          setProductCategoriesPageSize={setProductCategoriesPageSize}
          productCategoriesSearch={productCategoriesSearch}
          setProductCategoriesSearch={setProductCategoriesSearch}
          productTypes={productTypes}
          productTypesLoading={productTypesLoading}
          createProductCategory={createProductCategory}
          updateProductCategory={updateProductCategory}
          toggleProductCategoryStatus={toggleProductCategoryStatus}
          refreshProductCategories={refreshProductCategories}
        />
      )}

      {currentTab === "products" && (
        <Product
          products={products}
          productsLoading={productsLoading}
          productsError={productsError}
          productsPagination={productsPagination}
          productsPage={productsPage}
          setProductsPage={setProductsPage}
          productsPageSize={productsPageSize}
          setProductsPageSize={setProductsPageSize}
          productsSearch={productsSearch}
          setProductsSearch={setProductsSearch}
          productTypes={productTypes}
          productCategories={productCategories}
          createProduct={createProduct}
          updateProduct={updateProduct}
          toggleProductStatus={toggleProductStatus}
          refreshProducts={refreshProducts}
          getPackageTypes={getPackageTypes}
        />
      )}
    </div>
  );
};

export default ManageProduct;
